<template>
  <div class="q-pa-xs">
    <div :class="$q.dark.mode ? 'text-grey-2' : 'text-primary'">
      <p class="text-body2 text-bold">Candlestick Map of {{ symbol }}</p>
    </div>

    <div v-if="store.state.authenticate.isAuthenticated">
      <div v-if="store.state.packages.packageStatus?.current_package == null">
        <MessageCard
          :displayMessage="'This feature is only avilable for paid subscriber. Please subscribe the package'"
          :buttonText="'Subscribe Now'"
          :buttonRoute="'/payment'"
        />
      </div>

      <div
        v-else-if="
          store.state.packages.packageStatus?.current_package?.expired_status
        "
      >
        <MessageCard
          :displayMessage="'Your daily signals has expired. Please contact for extension'"
          :buttonText="'Subscribe Now'"
          :buttonRoute="'/payment'"
        />
      </div>

      <div v-else>
        <q-tabs
          v-model="tab"
          dense
          inline-label
          outside-arrows
          mobile-arrows
          :class="$q.dark.mode ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
          active-color="white"
          :active-bg-color="$q.dark.mode ? 'grey-9' : 'blue-4'"
          :indicator-color="$q.dark.mode ? 'grey-7' : 'blue-5'"
          align="justify"
          narrow-indicator
        >
          <q-tab name="candlestick" label="Candlestick" no-caps />
          <q-tab name="true_candlestick" label="True Candlestick" no-caps />
        </q-tabs>

        <q-tab-panels v-model="tab" animated>
          <q-tab-panel class="q-pa-none" name="candlestick">
            <div
              v-for="(patterns, bullish_or_bearish, index) in candlestickSignal"
              :key="bullish_or_bearish"
              :class="index < 2 ? 'col-12 col-sm-12' : 'col-12 col-sm-12'"
              class="q-py-xs"
            >
              <q-card flat bordered>
                <div
                  class="text-subtitle2 text-bold q-pa-xs"
                  :class="$q.dark.mode ? 'text-grey-2' : 'text-primary'"
                >
                  {{ bullish_or_bearish }}
                </div>
                <q-separator />

                <div class="row">
                  <div
                    :class="index < 2 ? 'col-6' : 'col-12'"
                    v-for="pattern_name in patterns"
                    :key="pattern_name"
                  >
                    <q-card
                      flat
                      bordered
                      @click="signalClick(bullish_or_bearish, pattern_name)"
                      class="q-pa-xs q-ma-xs cursor-pointer"
                      :class="{
                        'bg-blue-8 ': isActive(
                          bullish_or_bearish,
                          pattern_name
                        ),
                      }"
                    >
                      <div
                        :class="{
                          'text-secondary text-caption text-bold': true,
                          'text-white': isActive(
                            bullish_or_bearish,
                            pattern_name
                          ),
                        }"
                      >
                        <span> {{ pattern_name }} </span>
                      </div>
                    </q-card>
                  </div>
                </div>
              </q-card>
            </div>
          </q-tab-panel>

          <q-tab-panel class="q-pa-none" name="true_candlestick">
            <div
              v-for="(
                patterns, bullish_or_bearish, index
              ) in trueCandlestickSignal"
              :key="bullish_or_bearish"
              :class="index < 2 ? 'col-12 col-sm-12' : 'col-12 col-sm-12'"
              class="q-py-xs"
            >
              <q-card flat bordered>
                <div
                  class="text-subtitle2 text-bold q-pa-xs"
                  :class="$q.dark.mode ? 'text-grey-2' : 'text-primary'"
                >
                  {{ bullish_or_bearish }}
                </div>
                <q-separator />

                <div class="row">
                  <div
                    :class="index < 2 ? 'col-6' : 'col-12'"
                    v-for="pattern_name in patterns"
                    :key="pattern_name"
                  >
                    <q-card
                      flat
                      bordered
                      @click="signalClick(bullish_or_bearish, pattern_name)"
                      class="q-pa-xs q-ma-xs cursor-pointer"
                      :class="{
                        'bg-blue-8 ': isActive(
                          bullish_or_bearish,
                          pattern_name
                        ),
                      }"
                    >
                      <div
                        :class="{
                          'text-secondary text-caption text-bold': true,
                          'text-white': isActive(
                            bullish_or_bearish,
                            pattern_name
                          ),
                        }"
                      >
                        <span> {{ pattern_name }} </span>
                      </div>
                    </q-card>
                  </div>
                </div>
              </q-card>
            </div>
          </q-tab-panel>
        </q-tab-panels>
      </div>
    </div>

    <div v-else>
      <MessageCard
        :displayMessage="'This feature is only available for logged-in users. Please log in to access it.'"
        :buttonText="'Login'"
        :buttonRoute="'/login'"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";

import useData from "src/use/data";
import MessageCard from "src/components/MessageCard.vue";

const $q = useQuasar();
const store = useStore();

const props = defineProps({
  symbol: {
    type: String,
  },
});
const emit = defineEmits(["child-event"]);

const {
  candleStickPatternSymbolwise,
  trueCandleStickAllPatternSymbolwise,
  candleStickPattern,
} = useData();

const candleStickData = ref([]);
const trueCandleStickData = ref([]);
const filterCandleStickData = ref([]);

const combineData = ref([]);
const arrayList = ref([]);
const tab = ref("true_candlestick");

const candlestickSignal = ref({
  BULLISH: [
    "Spinning Top",
    "Pin Bar",
    "Bullish Kicker",
    "Bullish Engulfing",
    "Bullish Fractal",
    "Morning Star",
    "Three White Soldiers",
    "Inverted Hammer",
    "Hammer",
    "Piercing",
    "Three Inside Up",
  ],
  BEARISH: [
    "Spinning Top",
    "Pin Bar",
    "Bearish Kicker",
    "Bearish Engulfing",
    "Bearish Fractal",
    "Evening Star",
    "Three Black Crows",
    "Hanging Man",
    "Shooting Star",
    "Dark Cloud Cover",
    "Three Down Pattern",
  ],
  INDECISION: ["Inside Bar"],
});

const trueCandlestickSignal = ref({
  BULLISH: [
    "Bullish Spinning Top",
    "Bullish Pin Bar",
    "Bullish Kicker",
    "Bullish Engulfing",
    "Bullish Fractal",
    "Morning Star",
    "Three White Soldiers",
    "Inverted Hammer",
    "Hammer",
    "Piercing",
    "Three Inside Up",
  ],
  BEARISH: [
    "Bearish Spinning Top",
    "Bearish Pin Bar",
    "Bearish Kicker",
    "Bearish Engulfing",
    "Bearish Fractal",
    "Evening Star",
    "Three Black Crows",
    "Hanging Man",
    "Shooting Star",
    "Dark Cloud Cover",
    "Three Down Pattern",
  ],
  INDECISION: ["Inside Bar"],
});

const loadData = (symbol) => {
  $q.loading.show({});
  filterCandleStickData.value = [];
  candleStickPatternSymbolwise(props.symbol)
    .then((res) => {
      candleStickData.value = res.data.map((obj) => {
        const candlestick_pattern =
          obj.single_body_candle ||
          obj.double_body_candle ||
          obj.triple_body_candle ||
          obj.four_body_candle ||
          obj.star_pattern;
        return { ...obj, candlestick_pattern };
      });

      $q.loading.hide();
    })
    .catch((err) => {
      $q.loading.hide();
    });

  trueCandleStickAllPatternSymbolwise(props.symbol)
    .then((res) => {
      trueCandleStickData.value = res.data.map((obj) => {
        const candlestick_pattern =
          obj.single_body_candle ||
          obj.double_body_candle ||
          obj.triple_body_candle ||
          obj.four_body_candle ||
          obj.star_pattern;
        return { ...obj, candlestick_pattern };
      });

      $q.loading.hide();
    })
    .catch((err) => {
      $q.loading.hide();
    });
};

function signalClick(bullish_or_bearish, pattern_name) {
  const myObject = { arg1: bullish_or_bearish, arg2: pattern_name };

  const includesObject = arrayList.value.some(
    (obj) => obj.arg1 === myObject.arg1 && obj.arg2 === myObject.arg2
  );

  if (includesObject) {
    // Remove the object from the list
    arrayList.value = arrayList.value.filter(
      (obj) => obj.arg1 !== myObject.arg1 || obj.arg2 !== myObject.arg2
    );
  } else {
    // Add the object to the list
    arrayList.value.push(myObject);
  }

  combineData.value = [];

  arrayList.value.forEach((item) => {
    if (tab.value === "candlestick") {
      filterCandleStickData.value = candleStickData.value.filter(
        (i) =>
          i.bull_or_bear === item.arg1 && i.candlestick_pattern === item.arg2
      );
    }
    if (tab.value === "true_candlestick") {
      filterCandleStickData.value = trueCandleStickData.value.filter(
        (i) => i.candlestick_pattern === item.arg2
      );
    }
    combineData.value = combineData.value.concat(filterCandleStickData.value);
  });

  emit("child-event", combineData.value);
}

const isActive = (bullish_or_bearish, pattern_name) => {
  return arrayList.value.some((item) => {
    return item.arg1 === bullish_or_bearish && item.arg2 === pattern_name;
  });
};

onMounted(() => {
  loadData(props.symbol);
});

watch(props, (newProps, oldProps) => {
  loadData(newProps.symbol);
});

watch(tab, (newTab, oldTab) => {
  if (newTab) {
    emit("child-event", []);
    arrayList.value = [];
  }
});
</script>
